import { useEffect } from 'react';

export const SITE_URL = 'https://segunadepoju.com';

export type JsonLdValue =
  | string
  | number
  | boolean
  | null
  | JsonLdValue[]
  | { [key: string]: JsonLdValue };

export interface SeoProps {
  pageTitle: string;
  description: string;
  path?: string;
  image?: string;
  type?: string;
  keywords?: string[];
  jsonLd?: Record<string, JsonLdValue> | Array<Record<string, JsonLdValue>>;
}

const setMetaTag = (
  attributeName: 'name' | 'property',
  key: string,
  content: string,
) => {
  let tag = document.head.querySelector(`meta[${attributeName}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const ensureCanonicalLink = (href: string) => {
  let link = document.head.querySelector("link[rel='canonical']");

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
};

const ensureJsonLdScript = (jsonLd: SeoProps['jsonLd']) => {
  if (!jsonLd) {
    const existing = document.getElementById('page-schema');
    if (existing) {
      existing.remove();
    }
    return;
  }

  let script = document.getElementById(
    'page-schema',
  ) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = 'page-schema';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(jsonLd);
};

const Seo = ({
  pageTitle,
  description,
  path = window.location.pathname,
  image = `${SITE_URL}/logo.ico`,
  type = 'website',
  keywords = [
    'Oluwasegun Adepoju',
    'software engineer',
    'frontend developer',
    'full-stack engineer',
    'React developer',
    'TypeScript engineer',
    'cloud architecture',
    'backend engineer',
    'mobile engineer',
    'blockchain engineer',
    'AI engineer',
  ],
  jsonLd,
}: SeoProps): null => {
  useEffect(() => {
    document.title = pageTitle;

    setMetaTag('name', 'description', description);
    setMetaTag('name', 'keywords', keywords.join(', '));
    setMetaTag('name', 'robots', 'index,follow,max-image-preview:large');
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', new URL(path, SITE_URL).toString());
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:site_name', 'Oluwasegun Adepoju');
    setMetaTag('property', 'og:locale', 'en_US');
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);
    setMetaTag('name', 'twitter:site', '@Oluwasegun_AA');

    ensureCanonicalLink(new URL(path, SITE_URL).toString());
    ensureJsonLdScript(jsonLd);
  }, [description, image, jsonLd, keywords, pageTitle, path, type]);

  return null;
};

export default Seo;
